import { QueryClient } from '@tanstack/react-query';

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Returns the HTTP status carried by an error thrown from an API client,
 * or `undefined` for plain `Error`s with no status. Duck-typed against
 * `{ status: number }` so any thrown shape that exposes `status` is
 * picked up — including the `ApiError` class Phase 5 (D55) introduces.
 */
function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Application-wide TanStack Query client.
 *
 * Conservative defaults so the rebuilt hooks behave predictably:
 * - 5-minute stale time avoids constant refetches for slow-changing data
 *   (templates, sites, shift types).
 * - No retry on 4xx because client errors won't fix themselves; let the
 *   inline error block prompt the user (D35 — A5).
 * - Refetch-on-focus off because the wizard's localStorage auto-save (D36 —
 *   A6) makes implicit network calls on focus undesirable.
 *
 * The 4xx guard duck-types `error.status`. Current salvaged API clients
 * throw plain `Error` with the extracted message — `getErrorStatus`
 * returns `undefined`, so retry falls through to the count check. Once
 * Phase 5 (D55) wraps fetch in an `ApiError` class with `status`, this
 * guard starts short-circuiting 4xx without changes here.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: FIVE_MINUTES,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});
